import React, { useState, useMemo, memo } from 'react';
import { formatTime } from '../utils/api';

function ScheduleDisplay({ schedule, activeTab = 'students' }) {
  if (!schedule || schedule.length === 0) {
    return (
      <div className="schedule-display">
        <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Расписание не найдено
        </p>
      </div>
    );
  }

  return (
    <div className="weekly-schedule">
      <div className="weekly-days-container">
        {schedule.map((day, index) => (
          <DayColumn key={day.date || `day-${index}`} day={day} activeTab={activeTab} />
        ))}
      </div>
    </div>
  );
}

const DayColumn = memo(({ day, activeTab }) => {
  // Мемоизируем вычисления даты
  const dateInfo = useMemo(() => {
    let date;
    let dateStr = 'Дата не указана';
    let dateFormatted = '';
    let isToday = false;
    
    if (day.date) {
      // Если дата в формате DD-MM-YYYY
      if (typeof day.date === 'string' && day.date.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [dayPart, monthPart, yearPart] = day.date.split('-');
        date = new Date(parseInt(yearPart), parseInt(monthPart) - 1, parseInt(dayPart));
      } 
      // Если дата в формате YYYY-MM-DD
      else if (typeof day.date === 'string' && day.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [yearPart, monthPart, dayPart] = day.date.split('-');
        date = new Date(parseInt(yearPart), parseInt(monthPart) - 1, parseInt(dayPart));
      } 
      // Если дата в формате DD.MM.YYYY
      else if (typeof day.date === 'string' && day.date.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
        const [dayPart, monthPart, yearPart] = day.date.split('.');
        date = new Date(parseInt(yearPart), parseInt(monthPart) - 1, parseInt(dayPart));
      }
      // Если дата в формате YYYY-MM-DDTHH:mm:ss или подобном
      else if (typeof day.date === 'string' && day.date.includes('T')) {
        date = new Date(day.date);
      }
      // Пробуем стандартный формат
      else {
        date = new Date(day.date);
      }
    }

    // Проверяем, что дата валидна
    if (date && !isNaN(date.getTime())) {
      // Проверяем, является ли дата сегодняшней
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dayDate = new Date(date);
      dayDate.setHours(0, 0, 0, 0);
      isToday = dayDate.getTime() === today.getTime();
      
      // Форматируем дату - название дня недели
      const weekday = date.toLocaleDateString('ru-RU', { weekday: 'long' });
      dateStr = weekday.charAt(0).toUpperCase() + weekday.slice(1);
      
      // Форматируем полную дату
      const dayNum = date.getDate();
      const month = date.toLocaleDateString('ru-RU', { month: 'long' });
      const year = date.getFullYear();
      dateFormatted = `${dayNum} ${month} ${year} г.`;
    } else {
      // Если дата невалидна, пробуем использовать day.day если есть
      if (day.day) {
        dateStr = day.day.split(',')[0];
        dateFormatted = day.day;
      }
    }
    
    return { dateStr, dateFormatted, isToday };
  }, [day.date, day.day]);

  const { dateStr, dateFormatted, isToday } = dateInfo;

  return (
    <div className={`day-card ${isToday ? 'today' : ''}`}>
      <div className="day-card-header">
        <div className="day-card-header-content">
          <div className="day-card-title">
            <div className="calendar-icon-wrapper">
              <i className="bi bi-calendar3 calendar-icon"></i>
            </div>
            <div className="day-card-date-info">
              <div className="day-of-week">{dateStr}</div>
              <div className="day-full-date">{dateFormatted}</div>
            </div>
          </div>
          <div className="day-card-stats">
            <div className="stats-label">Всего занятий</div>
            <div className="stats-count">{day.lessons ? day.lessons.length : 0}</div>
          </div>
        </div>
      </div>
      <div className="day-card-content">
        {!day.lessons || day.lessons.length === 0 ? (
          <p className="no-lessons">Занятий нет</p>
        ) : (
          day.lessons.map((lesson, index) => {
            // Получаем номер пары из timewindow.description или используем индекс + 1
            const pairNumber = (lesson.timewindow?.description && lesson.timewindow.description.trim() !== '') 
              ? lesson.timewindow.description 
              : (index + 1);
            return (
              <LessonCard 
                key={lesson.id || `lesson-${day.date}-${index}`} 
                lesson={lesson} 
                pairNumber={pairNumber} 
                activeTab={activeTab} 
              />
            );
          })
        )}
      </div>
    </div>
  );
});

const LessonCard = memo(({ lesson, pairNumber, activeTab }) => {
  const [showAllGroups, setShowAllGroups] = useState(false);
  
  // Мемоизируем вычисления стилей и данных (не зависящие от showAllGroups)
  const { styles, brandColor, hasTime, timeBegin, timeEnd, groups, hasMoreGroups, hiddenCount } = useMemo(() => {
    // Определяем фирменный цвет организации
    const isCollegeTheme = document.body.classList.contains('college-theme');
    const brandColor = isCollegeTheme ? '#0058AA' : '#009F9B';
    
    // Определяем стили для типа занятия
    const getTypeStyles = (type) => {
      if (isCollegeTheme) {
        // УРК
        const typeMap = {
          'Аудиторные занятия': { bg: '#D06868', bgLight: 'rgba(208, 104, 104, 0.1)', text: '#ffffff' },
          'Практика': { bg: '#0058AA', bgLight: 'rgba(0, 88, 170, 0.1)', text: '#ffffff' },
          'Лекция': { bg: '#0058AA', bgLight: 'rgba(0, 88, 170, 0.1)', text: '#ffffff' },
          'Лабораторная': { bg: '#0058AA', bgLight: 'rgba(0, 88, 170, 0.1)', text: '#ffffff' }
        };
        return typeMap[type] || { bg: '#0058AA', bgLight: 'rgba(0, 88, 170, 0.1)', text: '#ffffff' };
      } else {
        // ЮУТУ
        const typeMap = {
          'Лекция': { bg: '#D06868', bgLight: 'rgba(208, 104, 104, 0.1)', text: '#ffffff' },
          'Практика': { bg: '#00A4A0', bgLight: 'rgba(0, 164, 160, 0.1)', text: '#ffffff' },
          'Аудиторные занятия': { bg: '#00A4A0', bgLight: 'rgba(0, 164, 160, 0.1)', text: '#ffffff' },
          'Лабораторная': { bg: '#00A4A0', bgLight: 'rgba(0, 164, 160, 0.1)', text: '#ffffff' }
        };
        return typeMap[type] || { bg: '#00A4A0', bgLight: 'rgba(0, 164, 160, 0.1)', text: '#ffffff' };
      }
    };

    const styles = getTypeStyles(lesson.type);
    const hasTime = lesson.timewindow && (lesson.timewindow.timebegin || lesson.timewindow.timeend);
    const timeBegin = lesson.timewindow?.timebegin ? formatTime(lesson.timewindow.timebegin) : null;
    const timeEnd = lesson.timewindow?.timeend ? formatTime(lesson.timewindow.timeend) : null;
    const groups = lesson.addition || [];
    const hasMoreGroups = groups.length > 3;
    const hiddenCount = groups.length - 3;
    
    return { styles, brandColor, hasTime, timeBegin, timeEnd, groups, hasMoreGroups, hiddenCount };
  }, [lesson.type, lesson.timewindow, lesson.addition]);
  
  // Вычисляем displayedGroups отдельно, так как зависит от состояния
  const displayedGroups = showAllGroups ? groups : groups.slice(0, 3);

  return (
    <div className="lesson-card-modern" style={{ '--lesson-color': styles.bg, '--lesson-bg-light': styles.bgLight }}>
      <div className="lesson-card-left-border"></div>
      <div className="lesson-card-body">
        <div className="lesson-left-column">
          <div className="lesson-pair-number-box" style={{ backgroundColor: brandColor }}>
            <div className="pair-number">{pairNumber}</div>
          </div>
          {hasTime && (
            <div className="pair-time-full" style={{ '--pair-time-bg': styles.bgLight, backgroundColor: brandColor }}>
              {timeBegin && <div className="pair-time-start">{timeBegin}</div>}
              {timeBegin && timeEnd && <div className="pair-time-separator"></div>}
              {timeEnd && <div className="pair-time-end">{timeEnd}</div>}
            </div>
          )}
        </div>
        <div className="lesson-main-info">
          <div className="lesson-header-modern">
            <h3 className="lesson-subject-name">{lesson.name || '—'}</h3>
            {lesson.type && (
              <span className="lesson-type-badge-modern" style={{ 
                backgroundColor: styles.bg, 
                color: styles.text,
                borderColor: styles.bg
              }}>
                {lesson.type}
              </span>
            )}
          </div>
          <div className="lesson-details-grid">
            {groups.length > 0 && (
              <div className="lesson-detail-item-modern">
                <div className="detail-icon-box">
                  <i className={`bi ${activeTab === 'students' ? 'bi-person-fill' : 'bi-people-fill'} detail-icon-modern`}></i>
                </div>
                <div className="detail-content">
                  <div className="detail-label-modern">{activeTab === 'students' ? 'Преподаватель' : 'Группа'}</div>
                  <div className="detail-value-modern">
                    {displayedGroups.join(', ')}
                    {hasMoreGroups && !showAllGroups && (
                      <span className="groups-more" onClick={() => setShowAllGroups(true)}>
                        {' '}и ещё {hiddenCount}
                      </span>
                    )}
                    {hasMoreGroups && showAllGroups && (
                      <span className="groups-less" onClick={() => setShowAllGroups(false)}>
                        {' '}(скрыть)
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {lesson.classroom && (
              <div className="lesson-detail-item-modern">
                <div className="detail-icon-box">
                  <i className="bi bi-geo-alt-fill detail-icon-modern"></i>
                </div>
                <div className="detail-content">
                  <div className="detail-label-modern">Аудитория</div>
                  <div className="detail-value-modern">{lesson.classroom}</div>
                </div>
              </div>
            )}
            {lesson.buildings && (
              <div className="lesson-detail-item-modern">
                <div className="detail-icon-box">
                  <i className="bi bi-building detail-icon-modern"></i>
                </div>
                <div className="detail-content">
                  <div className="detail-label-modern">Адрес</div>
                  <div className="detail-value-modern">{lesson.buildings}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default ScheduleDisplay;


